/**
 * Slide Type Detector Utility
 * Detects and handles both HTML and GSAP slide formats
 */

import {
  SlideFormat,
  GsapSlideData,
  UnifiedSlide,
  extractGsapMetadata,
  extractGsapTitle,
  calculateGsapDuration
} from '../types/gsapSlide';

/**
 * Detect slide format from file path
 */
export function detectSlideFormat(path: string): SlideFormat {
  if (path.endsWith('.json')) {
    return 'gsap';
  }
  return 'html';
}

/**
 * Extract slide number from path (works for both formats)
 */
export function extractSlideNumber(path: string): string {
  const match = path.match(/slide-?(\d+)/i);
  return match ? match[1] : '0';
}

/**
 * Extract title from HTML content
 */
export function extractHtmlTitle(content: string): string {
  const titleMatch = content.match(/<title>(.*?)<\/title>/i);
  if (titleMatch) {
    return titleMatch[1].replace(/^Slide \d+:\s*/, '');
  }

  const h1Match = content.match(/<h1[^>]*>(.*?)<\/h1>/i);
  return h1Match ? h1Match[1] : 'Untitled Scene';
}

/**
 * Parse GSAP slide JSON and convert to UnifiedSlide format
 */
export async function parseGsapSlide(
  path: string,
  jsonContent: string,
  slideNumber?: string
): Promise<UnifiedSlide> {
  try {
    const gsapData: GsapSlideData = JSON.parse(jsonContent);
    const number = slideNumber || extractSlideNumber(path);

    return {
      id: gsapData.id || `gsap-${number}`,
      number,
      title: extractGsapTitle(gsapData),
      path,
      format: 'gsap',
      gsapData,
      duration: calculateGsapDuration(gsapData),
      layoutType: gsapData.metadata?.layout_type,
      metadata: extractGsapMetadata(gsapData),
    };
  } catch (error) {
    console.error('Failed to parse GSAP slide JSON:', error);
    throw new Error(`Invalid GSAP slide JSON at ${path}`);
  }
}

/**
 * Convert HTML slide to UnifiedSlide format
 */
export function parseHtmlSlide(
  id: string,
  number: string,
  path: string,
  content: string
): UnifiedSlide {
  return {
    id,
    number,
    title: extractHtmlTitle(content),
    path,
    format: 'html',
    content,
  };
}

/**
 * Check if a slide path is GSAP format
 */
export function isGsapSlide(path: string): boolean {
  return detectSlideFormat(path) === 'gsap';
}

/**
 * Check if a slide path is HTML format
 */
export function isHtmlSlide(path: string): boolean {
  return detectSlideFormat(path) === 'html';
}

/**
 * Generate GSAP viewer URL with appropriate parameters
 */
export function buildGsapViewerUrl(
  slidePath: string,
  options: {
    editMode?: boolean;
    controls?: boolean;
    conversationId?: string;
  } = {}
): string {
  const { editMode = false, controls = true, conversationId } = options;

  const params = new URLSearchParams();
  params.set('slide', slidePath);
  params.set('editMode', editMode.toString());
  params.set('controls', controls.toString());

  if (conversationId) {
    params.set('conversationId', conversationId);
  }

  return `/gsap_framework/single-slide.html?${params.toString()}`;
}

/**
 * Format duration in human-readable format (matches existing SlideGallery format)
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

/**
 * Format time as MM:SS (for playback displays)
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
